/**
 * Flow Automation Storage Service
 *
 * Provides CRUD operations for flows, version control, execution tracking,
 * and statistics. Uses localStorage for persistence with an API-ready architecture.
 */

import type {
  Flow,
  FlowType,
  FlowStatus,
  FlowElement,
  FlowVariable,
  FlowConnector,
  FlowVersion,
  FlowRun,
  FlowRunStatus,
  FlowStats,
  SingleFlowStats,
  FlowSummary,
  FlowValidationResult,
  FlowValidationError,
  CreateFlowPayload,
  UpdateFlowPayload,
  CloneFlowPayload,
  FlowElementInput,
  FlowVariableInput,
  FlowConnectorInput,
  FlowTypeDefinition,
  ElementTypeDefinition,
  ElementType,
  FlowCondition,
  FlowConditionInput
} from '../types/flowAutomation';

// ============================================
// CONSTANTS
// ============================================

const FLOWS_STORAGE_KEY = 'crm_flows';
const FLOW_RUNS_STORAGE_KEY = 'crm_flow_runs';
const FLOW_VERSIONS_STORAGE_KEY = 'crm_flow_versions';

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Generate a unique ID with optional prefix
 */
const generateId = (prefix: string = 'flow'): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Get current timestamp in ISO format
 */
const now = (): string => new Date().toISOString();

/**
 * Generate API name from display name
 */
const generateApiName = (name: string): string => {
  return name
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
};

/**
 * Ensure element has an ID
 */
const ensureElementId = (element: FlowElementInput): FlowElement => ({
  ...element,
  id: element.id || generateId('el'),
  connectors: (element.connectors || []).map(c => ({
    ...c,
    id: c.id || generateId('conn'),
  })),
  faultConnector: element.faultConnector
    ? { ...element.faultConnector, id: element.faultConnector.id || generateId('fault') }
    : undefined,
} as FlowElement);

/**
 * Ensure variable has an ID
 */
const ensureVariableId = (variable: FlowVariableInput): FlowVariable => ({
  ...variable,
  id: variable.id || generateId('var'),
  isInput: variable.isInput ?? false,
  isOutput: variable.isOutput ?? false,
  isCollection: variable.isCollection ?? false,
} as FlowVariable);

/**
 * Ensure condition has an ID
 */
const ensureConditionId = (condition: FlowConditionInput): FlowCondition => ({
  ...condition,
  id: condition.id || generateId('cond'),
  rightValueType: condition.rightValueType ?? 'literal',
});

// ============================================
// FLOW TYPE DEFINITIONS
// ============================================

export const FLOW_TYPE_DEFINITIONS: FlowTypeDefinition[] = [
  {
    type: 'screen_flow',
    label: 'Screen Flow',
    description: 'Interactive flow with user screens and forms',
    icon: 'Monitor',
    color: 'blue',
    capabilities: ['screens', 'user_input', 'decisions', 'data_operations', 'integrations'],
  },
  {
    type: 'auto_launched',
    label: 'Auto-Launched Flow',
    description: 'Background flow triggered by other processes',
    icon: 'Zap',
    color: 'purple',
    capabilities: ['decisions', 'data_operations', 'integrations', 'subflows'],
  },
  {
    type: 'scheduled',
    label: 'Scheduled Flow',
    description: 'Flow that runs on a recurring schedule',
    icon: 'Clock',
    color: 'green',
    capabilities: ['decisions', 'data_operations', 'batch_processing', 'integrations'],
  },
  {
    type: 'record_triggered',
    label: 'Record-Triggered Flow',
    description: 'Automatically runs when records change',
    icon: 'Database',
    color: 'orange',
    capabilities: ['decisions', 'data_operations', 'field_updates', 'integrations'],
  },
  {
    type: 'platform_event',
    label: 'Platform Event Flow',
    description: 'Triggered by platform events',
    icon: 'Radio',
    color: 'red',
    capabilities: ['decisions', 'data_operations', 'event_handling', 'integrations'],
  },
];

export const ELEMENT_TYPE_DEFINITIONS: ElementTypeDefinition[] = [
  {
    type: 'start',
    label: 'Start',
    description: 'Flow entry point',
    icon: 'Play',
    category: 'flow_control',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 1,
    configurable: false,
  },
  {
    type: 'screen',
    label: 'Screen',
    description: 'Display a screen to users',
    icon: 'Monitor',
    category: 'interaction',
    availableIn: ['screen_flow'],
    hasConnector: true,
    maxConnectors: 1,
    configurable: true,
  },
  {
    type: 'decision',
    label: 'Decision',
    description: 'Branch based on conditions',
    icon: 'GitBranch',
    category: 'logic',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 10,
    configurable: true,
  },
  {
    type: 'assignment',
    label: 'Assignment',
    description: 'Set variable values',
    icon: 'Edit3',
    category: 'logic',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 1,
    configurable: true,
  },
  {
    type: 'loop',
    label: 'Loop',
    description: 'Iterate over a collection',
    icon: 'Repeat',
    category: 'logic',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 2,
    configurable: true,
  },
  {
    type: 'get_records',
    label: 'Get Records',
    description: 'Query records from database',
    icon: 'Search',
    category: 'data',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 1,
    configurable: true,
  },
  {
    type: 'create_records',
    label: 'Create Records',
    description: 'Create new records',
    icon: 'Plus',
    category: 'data',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 1,
    configurable: true,
  },
  {
    type: 'update_records',
    label: 'Update Records',
    description: 'Update existing records',
    icon: 'RefreshCw',
    category: 'data',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 1,
    configurable: true,
  },
  {
    type: 'delete_records',
    label: 'Delete Records',
    description: 'Delete records',
    icon: 'Trash2',
    category: 'data',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 1,
    configurable: true,
  },
  {
    type: 'action',
    label: 'Action',
    description: 'Call an invocable action',
    icon: 'Zap',
    category: 'integration',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 1,
    configurable: true,
  },
  {
    type: 'subflow',
    label: 'Subflow',
    description: 'Call another flow',
    icon: 'GitMerge',
    category: 'integration',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 1,
    configurable: true,
  },
  {
    type: 'email_alert',
    label: 'Email Alert',
    description: 'Send an email',
    icon: 'Mail',
    category: 'integration',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 1,
    configurable: true,
  },
  {
    type: 'post_to_feed',
    label: 'Post to Feed',
    description: 'Post to activity feed',
    icon: 'MessageSquare',
    category: 'integration',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 1,
    configurable: true,
  },
  {
    type: 'wait',
    label: 'Wait',
    description: 'Pause flow execution',
    icon: 'Clock',
    category: 'flow_control',
    availableIn: ['auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: true,
    maxConnectors: 2,
    configurable: true,
  },
  {
    type: 'end',
    label: 'End',
    description: 'Flow exit point',
    icon: 'Square',
    category: 'flow_control',
    availableIn: ['screen_flow', 'auto_launched', 'scheduled', 'record_triggered', 'platform_event'],
    hasConnector: false,
    maxConnectors: 0,
    configurable: false,
  },
];

// ============================================
// STORAGE SERVICE CLASS
// ============================================

class FlowAutomationStorage {
  // ============================================
  // PRIVATE METHODS
  // ============================================

  /**
   * Get all flows from localStorage
   */
  private loadFlows(): Flow[] {
    try {
      const stored = localStorage.getItem(FLOWS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading flows:', error);
      return [];
    }
  }

  /**
   * Save flows to localStorage
   */
  private saveFlows(flows: Flow[]): void {
    try {
      localStorage.setItem(FLOWS_STORAGE_KEY, JSON.stringify(flows));
    } catch (error) {
      console.error('Error saving flows:', error);
    }
  }

  /**
   * Get all flow runs from localStorage
   */
  private loadFlowRuns(): FlowRun[] {
    try {
      const stored = localStorage.getItem(FLOW_RUNS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading flow runs:', error);
      return [];
    }
  }

  /**
   * Save flow runs to localStorage
   */
  private saveFlowRuns(runs: FlowRun[]): void {
    try {
      localStorage.setItem(FLOW_RUNS_STORAGE_KEY, JSON.stringify(runs));
    } catch (error) {
      console.error('Error saving flow runs:', error);
    }
  }

  /**
   * Generate unique API name for a flow
   */
  private generateUniqueApiName(name: string, existingFlows: Flow[]): string {
    let apiName = generateApiName(name);
    let suffix = 1;

    while (existingFlows.some(f => f.apiName === apiName)) {
      apiName = `${generateApiName(name)}_${suffix}`;
      suffix++;
    }

    return apiName;
  }

  // ============================================
  // FLOW CRUD OPERATIONS
  // ============================================

  /**
   * Get all flows
   */
  getAllFlows(): Flow[] {
    return this.loadFlows();
  }

  /**
   * Get a single flow by ID
   */
  getFlow(id: string): Flow | null {
    const flows = this.loadFlows();
    return flows.find(f => f.id === id) || null;
  }

  /**
   * Get flows by type
   */
  getFlowsByType(type: FlowType): Flow[] {
    const flows = this.loadFlows();
    return flows.filter(f => f.type === type);
  }

  /**
   * Get flows by status
   */
  getFlowsByStatus(status: FlowStatus): Flow[] {
    const flows = this.loadFlows();
    return flows.filter(f => f.status === status);
  }

  /**
   * Search flows by name or description
   */
  searchFlows(query: string): Flow[] {
    const flows = this.loadFlows();
    const lowerQuery = query.toLowerCase();
    return flows.filter(
      f =>
        f.name.toLowerCase().includes(lowerQuery) ||
        f.description.toLowerCase().includes(lowerQuery) ||
        f.apiName.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Create a new flow
   */
  createFlow(payload: CreateFlowPayload): Flow {
    const flows = this.loadFlows();
    const timestamp = now();
    const id = generateId('flow');

    // Process elements and variables
    const elements = (payload.elements || []).map(ensureElementId);
    const variables = (payload.variables || []).map(ensureVariableId);

    // Create start element if not provided
    let startElementId = payload.startElementId;
    if (!startElementId && elements.length === 0) {
      const startElement: FlowElement = {
        id: generateId('el'),
        type: 'start',
        name: 'Start',
        label: 'Start',
        position: { x: 100, y: 100 },
        connectors: [],
        config: {},
      };
      elements.push(startElement);
      startElementId = startElement.id;
    } else if (!startElementId && elements.length > 0) {
      const startEl = elements.find(e => e.type === 'start');
      startElementId = startEl?.id || elements[0].id;
    }

    const newFlow: Flow = {
      id,
      name: payload.name,
      apiName: payload.apiName || this.generateUniqueApiName(payload.name, flows),
      description: payload.description,
      type: payload.type,
      status: 'draft',
      variables,
      elements,
      startElementId: startElementId!,
      scheduleConfig: payload.scheduleConfig,
      recordTriggerConfig: payload.recordTriggerConfig,
      platformEventConfig: payload.platformEventConfig,
      version: 1,
      versions: [
        {
          id: generateId('ver'),
          version: 1,
          status: 'draft',
          description: 'Initial version',
          createdDate: timestamp,
          createdBy: 'current_user',
          createdByName: 'System Administrator',
        },
      ],
      createdDate: timestamp,
      createdBy: 'current_user',
      createdByName: 'System Administrator',
      lastModifiedDate: timestamp,
      lastModifiedBy: 'current_user',
      lastModifiedByName: 'System Administrator',
      runCount: 0,
      successCount: 0,
      failureCount: 0,
    };

    flows.push(newFlow);
    this.saveFlows(flows);

    return newFlow;
  }

  /**
   * Update an existing flow
   */
  updateFlow(payload: UpdateFlowPayload): Flow | null {
    const flows = this.loadFlows();
    const index = flows.findIndex(f => f.id === payload.id);

    if (index === -1) return null;

    const existing = flows[index];
    const timestamp = now();

    // Extract complex fields that need processing
    const { variables, elements, ...safeProps } = payload;

    const updated: Flow = {
      ...existing,
      ...safeProps,
      status: (safeProps.status ?? existing.status) as FlowStatus,
      lastModifiedDate: timestamp,
      lastModifiedBy: 'current_user',
      lastModifiedByName: 'System Administrator',
    };

    // Process elements if provided
    if (elements) {
      updated.elements = elements.map(ensureElementId);
    }

    // Process variables if provided
    if (variables) {
      updated.variables = variables.map(ensureVariableId);
    }

    flows[index] = updated;
    this.saveFlows(flows);

    return updated;
  }

  /**
   * Delete a flow
   */
  deleteFlow(id: string): boolean {
    const flows = this.loadFlows();
    const filtered = flows.filter(f => f.id !== id);

    if (filtered.length === flows.length) return false;

    this.saveFlows(filtered);

    // Also delete associated runs
    const runs = this.loadFlowRuns();
    const filteredRuns = runs.filter(r => r.flowId !== id);
    this.saveFlowRuns(filteredRuns);

    return true;
  }

  // ============================================
  // STATUS MANAGEMENT
  // ============================================

  /**
   * Activate a flow
   */
  activateFlow(id: string): Flow | null {
    const flows = this.loadFlows();
    const index = flows.findIndex(f => f.id === id);

    if (index === -1) return null;

    const flow = flows[index];

    // Validate flow before activation
    const validation = this.validateFlow(flow);
    if (!validation.isValid) {
      console.error('Cannot activate invalid flow:', validation.errors);
      return null;
    }

    const timestamp = now();

    // Update current version status
    const updatedVersions = flow.versions.map(v =>
      v.version === flow.version
        ? {
            ...v,
            status: 'active' as FlowStatus,
            activatedDate: timestamp,
            activatedBy: 'current_user',
            activatedByName: 'System Administrator',
          }
        : v
    );

    const updated: Flow = {
      ...flow,
      status: 'active',
      versions: updatedVersions,
      lastModifiedDate: timestamp,
      lastModifiedBy: 'current_user',
      lastModifiedByName: 'System Administrator',
    };

    flows[index] = updated;
    this.saveFlows(flows);

    return updated;
  }

  /**
   * Deactivate a flow
   */
  deactivateFlow(id: string): Flow | null {
    const flows = this.loadFlows();
    const index = flows.findIndex(f => f.id === id);

    if (index === -1) return null;

    const flow = flows[index];
    const timestamp = now();

    // Update current version status
    const updatedVersions = flow.versions.map(v =>
      v.version === flow.version
        ? {
            ...v,
            status: 'inactive' as FlowStatus,
            deactivatedDate: timestamp,
          }
        : v
    );

    const updated: Flow = {
      ...flow,
      status: 'inactive',
      versions: updatedVersions,
      lastModifiedDate: timestamp,
      lastModifiedBy: 'current_user',
      lastModifiedByName: 'System Administrator',
    };

    flows[index] = updated;
    this.saveFlows(flows);

    return updated;
  }

  /**
   * Clone a flow
   */
  cloneFlow(payload: CloneFlowPayload): Flow | null {
    const flows = this.loadFlows();
    const original = flows.find(f => f.id === payload.sourceFlowId);

    if (!original) return null;

    const timestamp = now();
    const newId = generateId('flow');

    // Deep clone elements with new IDs
    const idMapping: Record<string, string> = {};
    const clonedElements = original.elements.map(el => {
      const newElId = generateId('el');
      idMapping[el.id] = newElId;
      return {
        ...el,
        id: newElId,
        connectors: el.connectors.map(c => ({
          ...c,
          id: generateId('conn'),
          targetElementId: '', // Will be updated after all elements have new IDs
        })),
        faultConnector: el.faultConnector
          ? { ...el.faultConnector, id: generateId('fault'), targetElementId: '' }
          : undefined,
      };
    });

    // Update connector target IDs
    clonedElements.forEach(el => {
      el.connectors = el.connectors.map(c => {
        const originalConnector = original.elements
          .find(oe => idMapping[oe.id] === el.id)
          ?.connectors.find(oc => oc.id !== c.id || true); // Find corresponding original connector

        if (originalConnector) {
          return { ...c, targetElementId: idMapping[originalConnector.targetElementId] || '' };
        }
        return c;
      });
    });

    // Clone variables with new IDs
    const clonedVariables = original.variables.map(v => ({
      ...v,
      id: generateId('var'),
    }));

    const clonedFlow: Flow = {
      ...original,
      id: newId,
      name: payload.newName,
      apiName: this.generateUniqueApiName(payload.newName, flows),
      description: payload.description || `Clone of ${original.name}`,
      status: 'draft',
      elements: clonedElements,
      variables: clonedVariables,
      startElementId: idMapping[original.startElementId] || clonedElements[0]?.id || '',
      version: 1,
      versions: [
        {
          id: generateId('ver'),
          version: 1,
          status: 'draft',
          description: `Cloned from ${original.name} v${original.version}`,
          createdDate: timestamp,
          createdBy: 'current_user',
          createdByName: 'System Administrator',
        },
      ],
      createdDate: timestamp,
      createdBy: 'current_user',
      createdByName: 'System Administrator',
      lastModifiedDate: timestamp,
      lastModifiedBy: 'current_user',
      lastModifiedByName: 'System Administrator',
      lastRunDate: undefined,
      runCount: 0,
      successCount: 0,
      failureCount: 0,
    };

    flows.push(clonedFlow);
    this.saveFlows(flows);

    return clonedFlow;
  }

  // ============================================
  // VERSION CONTROL
  // ============================================

  /**
   * Create a new version of a flow
   */
  createNewVersion(id: string, description?: string): FlowVersion | null {
    const flows = this.loadFlows();
    const index = flows.findIndex(f => f.id === id);

    if (index === -1) return null;

    const flow = flows[index];
    const timestamp = now();
    const newVersionNum = flow.version + 1;

    const newVersion: FlowVersion = {
      id: generateId('ver'),
      version: newVersionNum,
      status: 'draft',
      description: description || `Version ${newVersionNum}`,
      createdDate: timestamp,
      createdBy: 'current_user',
      createdByName: 'System Administrator',
    };

    const updated: Flow = {
      ...flow,
      version: newVersionNum,
      status: 'draft',
      versions: [...flow.versions, newVersion],
      lastModifiedDate: timestamp,
      lastModifiedBy: 'current_user',
      lastModifiedByName: 'System Administrator',
    };

    flows[index] = updated;
    this.saveFlows(flows);

    return newVersion;
  }

  /**
   * Get flow versions
   */
  getFlowVersions(id: string): FlowVersion[] {
    const flow = this.getFlow(id);
    return flow?.versions || [];
  }

  // ============================================
  // EXECUTION TRACKING
  // ============================================

  /**
   * Get all flow runs
   */
  getAllFlowRuns(): FlowRun[] {
    return this.loadFlowRuns();
  }

  /**
   * Get flow runs for a specific flow
   */
  getFlowRuns(flowId: string): FlowRun[] {
    const runs = this.loadFlowRuns();
    return runs.filter(r => r.flowId === flowId);
  }

  /**
   * Get recent flow runs
   */
  getRecentFlowRuns(limit: number = 50): FlowRun[] {
    const runs = this.loadFlowRuns();
    return runs
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
      .slice(0, limit);
  }

  /**
   * Record a flow run
   */
  recordFlowRun(run: Omit<FlowRun, 'id'>): FlowRun {
    const runs = this.loadFlowRuns();

    const newRun: FlowRun = {
      ...run,
      id: generateId('run'),
    };

    runs.push(newRun);
    this.saveFlowRuns(runs);

    // Update flow statistics
    const flows = this.loadFlows();
    const flowIndex = flows.findIndex(f => f.id === run.flowId);

    if (flowIndex !== -1) {
      const flow = flows[flowIndex];
      flows[flowIndex] = {
        ...flow,
        lastRunDate: run.startTime,
        runCount: flow.runCount + 1,
        successCount: run.status === 'completed' ? flow.successCount + 1 : flow.successCount,
        failureCount: run.status === 'failed' ? flow.failureCount + 1 : flow.failureCount,
      };
      this.saveFlows(flows);
    }

    return newRun;
  }

  /**
   * Update a flow run status
   */
  updateFlowRun(
    runId: string,
    updates: Partial<Pick<FlowRun, 'status' | 'endTime' | 'duration' | 'errorMessage' | 'errorElement' | 'elementsExecuted' | 'outputVariables'>>
  ): FlowRun | null {
    const runs = this.loadFlowRuns();
    const index = runs.findIndex(r => r.id === runId);

    if (index === -1) return null;

    const updatedRun = { ...runs[index], ...updates };
    runs[index] = updatedRun;
    this.saveFlowRuns(runs);

    return updatedRun;
  }

  // ============================================
  // STATISTICS
  // ============================================

  /**
   * Get overall flow statistics
   */
  getStats(): FlowStats {
    const flows = this.loadFlows();
    const runs = this.loadFlowRuns();

    const now24h = Date.now() - 24 * 60 * 60 * 1000;
    const now7d = Date.now() - 7 * 24 * 60 * 60 * 1000;

    const byType: Record<FlowType, number> = {
      screen_flow: 0,
      auto_launched: 0,
      scheduled: 0,
      record_triggered: 0,
      platform_event: 0,
    };

    flows.forEach(f => {
      byType[f.type]++;
    });

    const completedRuns = runs.filter(r => r.status === 'completed');
    const failedRuns = runs.filter(r => r.status === 'failed');
    const runningRuns = runs.filter(r => r.status === 'running' || r.status === 'waiting');

    const totalDuration = completedRuns.reduce((sum, r) => sum + (r.duration || 0), 0);
    const averageRunTime = completedRuns.length > 0 ? totalDuration / completedRuns.length : 0;

    return {
      totalFlows: flows.length,
      activeFlows: flows.filter(f => f.status === 'active').length,
      draftFlows: flows.filter(f => f.status === 'draft').length,
      inactiveFlows: flows.filter(f => f.status === 'inactive').length,
      byType,
      totalRuns: runs.length,
      successfulRuns: completedRuns.length,
      failedRuns: failedRuns.length,
      runningFlows: runningRuns.length,
      averageRunTime,
      runsLast24Hours: runs.filter(r => new Date(r.startTime).getTime() > now24h).length,
      runsLast7Days: runs.filter(r => new Date(r.startTime).getTime() > now7d).length,
    };
  }

  /**
   * Get statistics for a single flow
   */
  getFlowStats(flowId: string): SingleFlowStats | null {
    const flow = this.getFlow(flowId);
    if (!flow) return null;

    const runs = this.getFlowRuns(flowId);
    const completedRuns = runs.filter(r => r.status === 'completed');
    const failedRuns = runs.filter(r => r.status === 'failed');

    const totalDuration = completedRuns.reduce((sum, r) => sum + (r.duration || 0), 0);
    const averageRunTime = completedRuns.length > 0 ? totalDuration / completedRuns.length : 0;

    const sortedRuns = [...runs].sort(
      (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
    );
    const lastRun = sortedRuns[0];

    return {
      flowId,
      totalRuns: runs.length,
      successfulRuns: completedRuns.length,
      failedRuns: failedRuns.length,
      averageRunTime,
      lastRunDate: lastRun?.startTime,
      lastRunStatus: lastRun?.status,
    };
  }

  // ============================================
  // FLOW SUMMARIES
  // ============================================

  /**
   * Get flow summaries for list display
   */
  getFlowSummaries(): FlowSummary[] {
    const flows = this.loadFlows();

    return flows.map(f => {
      const successRate = f.runCount > 0 ? (f.successCount / f.runCount) * 100 : 0;

      return {
        id: f.id,
        name: f.name,
        apiName: f.apiName,
        description: f.description,
        type: f.type,
        status: f.status,
        version: f.version,
        lastModifiedDate: f.lastModifiedDate,
        lastModifiedByName: f.lastModifiedByName,
        lastRunDate: f.lastRunDate,
        runCount: f.runCount,
        successRate: Math.round(successRate * 10) / 10,
      };
    });
  }

  // ============================================
  // VALIDATION
  // ============================================

  /**
   * Validate a flow
   */
  validateFlow(flow: Flow): FlowValidationResult {
    const errors: FlowValidationError[] = [];
    const warnings: FlowValidationError[] = [];

    // Check for start element
    if (!flow.startElementId) {
      errors.push({
        severity: 'error',
        message: 'Flow must have a start element',
        suggestion: 'Add a Start element to the flow',
      });
    }

    const startElement = flow.elements.find(e => e.id === flow.startElementId);
    if (!startElement) {
      errors.push({
        severity: 'error',
        message: 'Start element not found',
        suggestion: 'Ensure the start element ID references a valid element',
      });
    }

    // Check for at least one element
    if (flow.elements.length === 0) {
      errors.push({
        severity: 'error',
        message: 'Flow must have at least one element',
        suggestion: 'Add elements to the flow',
      });
    }

    // Check for disconnected elements
    const connectedElements = new Set<string>();
    const collectConnected = (elementId: string) => {
      if (connectedElements.has(elementId)) return;
      connectedElements.add(elementId);

      const element = flow.elements.find(e => e.id === elementId);
      if (element) {
        element.connectors.forEach(c => {
          if (c.targetElementId) {
            collectConnected(c.targetElementId);
          }
        });
      }
    };

    if (flow.startElementId) {
      collectConnected(flow.startElementId);
    }

    flow.elements.forEach(e => {
      if (!connectedElements.has(e.id) && e.type !== 'end') {
        warnings.push({
          elementId: e.id,
          elementName: e.name,
          severity: 'warning',
          message: `Element "${e.name}" is not connected to the flow`,
          suggestion: 'Connect this element to the flow or remove it',
        });
      }
    });

    // Check for end elements (for non-auto-launched flows)
    if (flow.type === 'screen_flow') {
      const hasEndElement = flow.elements.some(e => e.type === 'end');
      if (!hasEndElement) {
        warnings.push({
          severity: 'warning',
          message: 'Flow does not have an End element',
          suggestion: 'Add an End element to explicitly terminate the flow',
        });
      }
    }

    // Check screen flow has screens
    if (flow.type === 'screen_flow') {
      const hasScreens = flow.elements.some(e => e.type === 'screen');
      if (!hasScreens) {
        warnings.push({
          severity: 'warning',
          message: 'Screen Flow has no screen elements',
          suggestion: 'Add at least one Screen element for user interaction',
        });
      }
    }

    // Check scheduled flow has schedule config
    if (flow.type === 'scheduled' && !flow.scheduleConfig) {
      errors.push({
        severity: 'error',
        message: 'Scheduled Flow requires schedule configuration',
        suggestion: 'Configure the schedule settings',
      });
    }

    // Check record-triggered flow has trigger config
    if (flow.type === 'record_triggered' && !flow.recordTriggerConfig) {
      errors.push({
        severity: 'error',
        message: 'Record-Triggered Flow requires trigger configuration',
        suggestion: 'Configure the record trigger settings',
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  /**
   * Initialize with mock data if empty
   */
  initializeWithMockData(mockFlows: Flow[], mockRuns: FlowRun[]): void {
    const existingFlows = this.loadFlows();

    if (existingFlows.length === 0) {
      this.saveFlows(mockFlows);
    }

    const existingRuns = this.loadFlowRuns();
    if (existingRuns.length === 0) {
      this.saveFlowRuns(mockRuns);
    }
  }

  /**
   * Reset all data
   */
  reset(): void {
    localStorage.removeItem(FLOWS_STORAGE_KEY);
    localStorage.removeItem(FLOW_RUNS_STORAGE_KEY);
    localStorage.removeItem(FLOW_VERSIONS_STORAGE_KEY);
  }
}

// ============================================
// EXPORT SINGLETON INSTANCE
// ============================================

export const flowAutomationStorage = new FlowAutomationStorage();

export default flowAutomationStorage;
